import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const spinnerVariants = cva(
  "inline-block animate-spin rounded-full border-2 border-current border-t-transparent text-muted-foreground",
  {
    variants: {
      size: {
        sm: "size-4 border-2",
        default: "size-6 border-2",
        lg: "size-10 border-3",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

/**
 * Spinner - antd Spin 대체 컴포넌트
 *
 * 사용 예시:
 *   <Spinner />
 *   <Spinner size="lg" />
 *   <Spinner spinning={loading}><Content /></Spinner>
 *   <Spinner tip="로딩 중..." />
 */
function Spinner({
  className,
  size,
  spinning = true,
  tip,
  children,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof spinnerVariants> & {
    spinning?: boolean;
    tip?: React.ReactNode;
  }) {
  const spinner = (
    <div
      data-slot="spinner"
      className={cn("flex flex-col items-center justify-center gap-2", className)}
      {...props}
    >
      <div
        role="status"
        aria-label="loading"
        className={cn(spinnerVariants({ size }))}
      />
      {tip && (
        <span className="text-xs text-muted-foreground">{tip}</span>
      )}
    </div>
  );

  // children 없으면 spinner만 렌더링
  if (!children) {
    return spinning ? spinner : null;
  }

  // children이 있으면 wrapper로 감싸서 오버레이
  return (
    <div data-slot="spinner-wrapper" className="relative">
      {spinning && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 rounded-md">
          {spinner}
        </div>
      )}
      <div className={cn(spinning && "pointer-events-none opacity-50")}>
        {children}
      </div>
    </div>
  );
}

export { Spinner, spinnerVariants };
